import { Box, styled, Theme, useMediaQuery } from '@mui/material';
import { UserRole } from 'business/definitions';
import * as React from 'react';
import { useEffect, useState } from 'react';

import {
    COLLAPSED_SIDEBAR_WIDTH,
    EASING_FUNCTION,
    EXPANDED_SIDEBAR_WIDTH,
    makeTransitions,
    TRANSITION_TIME,
} from 'styles/theme';
import { useSideBarMode } from './hooks';
import SideBar from './SideBar';

type Props = {
    children: React.ReactNode;
    isAuthenticated: boolean;
    isAdmin: boolean;
    userRoles: UserRole[];
    logout: () => void;
    client: ClientT;
    homePageUrl: string;
    isMaintenance: boolean;
    isPublicDashboard: boolean;
};

const StyAppWrapper = styled(Box, {
    name: 'StyAppWrapper',
    shouldForwardProp: (prop) =>
        prop !== 'modeIsPreferCollapsed' &&
        prop !== 'animate' &&
        prop !== 'shouldHideSidebar',
})<{
    modeIsPreferCollapsed: boolean;
    animate: boolean;
    shouldHideSidebar: boolean;
}>(({ shouldHideSidebar, modeIsPreferCollapsed, animate, theme }) => ({
    ...(modeIsPreferCollapsed
        ? {
              // The whole purpose of this animation is to move the body of the app along
              // with the sidebar when we toggle the sidebar mode.
              '@keyframes toPreferCollapsed': {
                  from: { marginLeft: EXPANDED_SIDEBAR_WIDTH }, // Width of expanded sidebar
                  to: { marginLeft: COLLAPSED_SIDEBAR_WIDTH }, // Width of collapsed sidebar (on desktop)
              },
              // The animate prop is here to make sure we do not run this animation on page load.
              animation: animate
                  ? `toPreferCollapsed ${TRANSITION_TIME}ms ${EASING_FUNCTION} 0ms`
                  : '',
              marginLeft: COLLAPSED_SIDEBAR_WIDTH,
              maxWidth: `calc(100% - ${COLLAPSED_SIDEBAR_WIDTH})`,
              [theme.breakpoints.down('sm')]: {
                  // On mobile, the side bar does not have padding and is thinner.
                  // we remove the padding of 1rem on each side.
                  marginLeft: '2.5rem',
              },
          }
        : {
              marginLeft: '0',
              maxWidth: `calc(100% - ${EXPANDED_SIDEBAR_WIDTH})`,
              transition: 'max-width 1s',
          }),
    '@media print': {
        marginLeft: '0',
    },
    flexGrow: 1,
    overflowX: 'auto',
    '& > *': {
        height: '100%',
    },
    // order is important, we want to override the previous rules instead of nesting ternaries
    ...(shouldHideSidebar && { marginLeft: '0', maxWidth: '100%' }),
}));

const StySideBarWrapper = styled(Box, {
    name: 'StySideBarWrapper',
    shouldForwardProp: (prop) =>
        prop !== 'modeIsPreferCollapsed' &&
        prop !== 'expanded' &&
        prop !== 'shouldHideSidebar',
})<{
    modeIsPreferCollapsed: boolean;
    expanded: boolean;
    shouldHideSidebar: boolean;
}>(({ modeIsPreferCollapsed, expanded, shouldHideSidebar }) => ({
    zIndex: 1, // necessary so Pendo tooltips on the main page don't overlap the side bar
    ...(modeIsPreferCollapsed
        ? {
              position: 'fixed' as const,
              height: '100vh',
              zIndex: 100,
              top: 0,
              left: 0,
          }
        : {}),
    ...(modeIsPreferCollapsed && expanded
        ? {
              boxShadow:
                  '0px 22px 15px -7px rgba(0,0,0,0.6),0px 48px 38px 3px rgba(0,0,0,0.5),0px 18px 46px 8px rgba(0,0,0,0.4)',
          }
        : {}),
    transition: makeTransitions(['box-shadow']),
    '& > nav': {
        height: '100vh',
    },
    ...(shouldHideSidebar && { display: 'none' }),
}));

export default function AppWithSideBar({
    children,
    isAuthenticated,
    isAdmin,
    userRoles,
    logout,
    client,
    homePageUrl,
    isMaintenance,
    isPublicDashboard,
}: Props) {
    const [shouldSetAppWrapperAnimation, setShouldSetAppWrapperAnimation] =
        useState(false);

    // on small screen we force the side bar to be small
    const isSmallScreen = useMediaQuery<Theme>((theme) =>
        theme.breakpoints.down('sm'),
    );

    // Two things control the SideBar:
    // - The SideBar mode. It's the user's preference and is stored in the local storage
    //   It can be:
    //   - either 'always expanded', the SideBar never collapses in this mode.
    //   - or 'prefer collapsed', meaning that the SideBar is collapsed per default
    //     and temporarily expands when the user tries to navigate, then collapses again.
    // - The expanded state. It's always true in the 'always expanded' mode and toggles
    //   on and off in the 'prefer collapsed' mode.
    const [sideBarMode, toggleSideBarMode] = useSideBarMode(isSmallScreen);
    const [expanded, setExpanded] = useState(sideBarMode === 'alwaysExpanded');
    const shouldHideSidebar = isMaintenance || isPublicDashboard;

    // Force the sidebar to be collapsed when the user switches to mobile
    // and expand it when the user switches to desktop and their preference is 'alwaysExpanded'
    useEffect(() => {
        if (isSmallScreen) {
            setExpanded(false);
        } else if (sideBarMode === 'alwaysExpanded') {
            setExpanded(true);
        }
    }, [isSmallScreen, sideBarMode]);

    return (
        <>
            <StySideBarWrapper
                modeIsPreferCollapsed={sideBarMode === 'preferCollapsed'}
                expanded={expanded}
                shouldHideSidebar={shouldHideSidebar}
            >
                <SideBar
                    sideBarMode={sideBarMode}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    toggleSideBarMode={() => {
                        setShouldSetAppWrapperAnimation(true);
                        toggleSideBarMode();
                    }}
                    isAuthenticated={isAuthenticated}
                    isAdmin={isAdmin}
                    userRoles={userRoles}
                    client={client}
                    logout={logout}
                    homePageUrl={homePageUrl}
                />
            </StySideBarWrapper>
            <StyAppWrapper
                animate={shouldSetAppWrapperAnimation}
                modeIsPreferCollapsed={sideBarMode === 'preferCollapsed'}
                shouldHideSidebar={shouldHideSidebar}
            >
                {children}
            </StyAppWrapper>
        </>
    );
}
